/**
 * Right Modal Component
 *
 * Modal for creating or editing a right (permission).
 * Features: form validation, name and description fields.
 */

import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconKey } from '@tabler/icons-react';
import { Modal, Stack, Button, Group, Alert, TextInput, Textarea } from '../../../../components/ui';
import { usePermissionManagement } from '../hooks/usePermissionManagement';
import type { RightModalProps } from '../types';

export function RightModal({ opened, onClose, onSuccess, right }: RightModalProps) {
  const permissionMgmt = usePermissionManagement();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEditMode = !!right;

  // Form setup with validation
  const form = useForm({
    initialValues: {
      name: right?.name || '',
      description: right?.description || '',
    },
    validate: {
      name: (value) => {
        if (!value || value.trim() === '') return 'Right name is required';
        if (value.trim().length < 2) return 'Right name must be at least 2 characters';
        if (value.trim().length > 50) return 'Right name must not exceed 50 characters';
        // Suggest dot notation for rights
        if (!/^[a-zA-Z0-9._-]+$/.test(value.trim())) {
          return 'Right name should only contain letters, numbers, dots, hyphens, and underscores';
        }
        return null;
      },
      description: (value) => {
        if (value && value.trim().length > 500) {
          return 'Description must not exceed 500 characters';
        }
        return null;
      },
    },
  });

  // Update form when right prop changes
  useEffect(() => {
    if (right) {
      form.setValues({
        name: right.name || '',
        description: right.description || '',
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [right]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: typeof form.values) => {
    setSubmitError(null);

    try {
      if (isEditMode) {
        // Update existing right
        await permissionMgmt.updateRight(right.id, {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
        });
      } else {
        // Create new right
        await permissionMgmt.createRight({
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
        });
      }

      form.reset();
      onSuccess();
    } catch (err: any) {
      setSubmitError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} right`);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    form.reset();
    setSubmitError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconKey size={20} />
          {isEditMode ? 'Edit Right' : 'Create New Right'}
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Error Alert */}
          {submitError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}

          {/* Right Name */}
          <TextInput
            label="Right Name"
            placeholder="e.g., users.create, reports.view"
            description="Use dot notation for grouping rights (e.g., users.create, users.edit)"
            required
            {...form.getInputProps('name')}
          />

          {/* Description */}
          <Textarea
            label="Description"
            placeholder="Describe what this right/permission allows users to do"
            rows={4}
            {...form.getInputProps('description')}
          />

          {/* Action Buttons */}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={permissionMgmt.loading} variant="gradient" gradientVariant="primary">
              {isEditMode ? 'Update Right' : 'Create Right'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
